import clsx from "clsx";
import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

interface HintProps {
  content: ReactNode;
  children: ReactNode;
  wrapperClassName?: string;
}

export function Hint({ content, children, wrapperClassName }: HintProps): React.JSX.Element {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ "aria-describedby"?: string }>, {
        "aria-describedby": id,
      })
    : children;

  function positionTooltip(): void {
    const tooltipEl = tooltipRef.current;
    if (!tooltipEl) return;

    const viewportPadding = 8;
    const offset = 12;
    const maxWidth = 400;

    const tooltipRect = tooltipEl.getBoundingClientRect();

    let x = pointerRef.current?.x;
    let y = pointerRef.current?.y;

    if (x == null || y == null) {
      const anchorRect = wrapperRef.current?.getBoundingClientRect();
      if (!anchorRect) return;
      x = anchorRect.left + anchorRect.width / 2;
      y = anchorRect.bottom;
    }

    const width = Math.min(maxWidth, tooltipRect.width || maxWidth);
    let left = x - width / 2;
    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - viewportPadding - width));

    const fitsBelow = y + offset + tooltipRect.height + viewportPadding <= window.innerHeight;
    const top = fitsBelow ? y + offset : Math.max(viewportPadding, y - offset - tooltipRect.height);

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.maxWidth = `${maxWidth}px`;
  }

  useEffect(() => {
    if (!open) return;
    positionTooltip();
    const handleScroll = () => positionTooltip();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [open]);

  return (
    <div
      ref={wrapperRef}
      className={clsx("relative", wrapperClassName)}
      onPointerEnter={() => setOpen(true)}
      onPointerLeave={() => setOpen(false)}
      onPointerMove={(e) => {
        pointerRef.current = { x: e.clientX, y: e.clientY };
        if (open) positionTooltip();
      }}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={() => setOpen(false)}
    >
      {trigger}
      {open && (
        <div ref={tooltipRef} id={id} role="tooltip" className="pointer-events-none fixed z-50">
          <div className="border-cyber-border bg-cyber-bg text-cyber-text max-w-[400px] rounded border px-2 py-2 text-center text-[11px] break-words whitespace-normal shadow">
            {content}
          </div>
        </div>
      )}
    </div>
  );
}
